import { useMemo, useState, Fragment } from 'react'
import { useParams } from 'react-router-dom'
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Box,
  Button,
  styled,
  CircularProgress,
  Badge
} from '@mui/material'
import { Edit, Close, QuestionMark, Movie } from '@mui/icons-material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  useProtocolRoom,
  useProtocolRoomImages,
  useControlQuestions,
  useProcotol,
  useControlQuestionAnswer
} from '@services/protocols/protocols.hook'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import ProtocolRoomImages from './ProtocolRoomImages'
import ProtocolRoomImagesUpload, { ImageWithComment } from './ProtocolRoomImagesUpload'
import ProtocolControlQuestions from './ProtocolControlQuestions'
import UploadProgress from '@components/UploadProgress'
import { getProtocols } from '@services/protocols/protocols.helpers'
import { uploadProgressStore } from '@services/uploadProgress/uploadProgress.store'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

function ProtocolRooms({ isReadOnly }: { isReadOnly: boolean }) {
  const [qestionRoomInstance, setQuestionRoomInstance] = useState<{
    id: number
    name: string
    label: string
  }>({
    id: -1,
    name: '',
    label: ''
  })
  const { id } = useParams<{ id: string }>()
  const { protocol, isLoading } = useProcotol(Number(id))
  const { currentProtocol, oppositeProtocol } = getProtocols(protocol)
  const { controlQuestionAnswer } = useControlQuestionAnswer(Number(id))
  const acceptanceProtocol =
    currentProtocol?.protocolType === PROTOCOL_TYPE.acceptance ? currentProtocol : oppositeProtocol

  const { protocolRoom: acceptanceProtocolRoom } = useProtocolRoom(Number(acceptanceProtocol?.id))
  const { protocolRoom, updateProtocolRoom } = useProtocolRoom(Number(id))
  const { controlQuestions } = useControlQuestions()

  const roomsWithQuestions = useMemo(
    () =>
      protocolRoom.map((room) => {
        const questions = controlQuestions.filter(
          (question) => question.roomType.name === room.roomInstance.name
        )
        const answersCount = controlQuestionAnswer.filter(
          (answer) => answer.room === room.id
        ).length

        return {
          ...room,
          questions,
          answersCount
        }
      }),
    [protocolRoom, controlQuestions, controlQuestionAnswer]
  )

  const acceptanceRoomsWithQuestions = useMemo(
    () =>
      acceptanceProtocolRoom.map((room) => {
        const questions = controlQuestions.filter(
          (question) => question.roomType.name === room.roomInstance.name
        )

        return {
          ...room,
          questions
        }
      }),
    [acceptanceProtocolRoom, controlQuestions]
  )

  const roomUpdateForm = useFormik({
    validateOnMount: false,
    validateOnChange: false,
    initialValues: {
      id: -1,
      description: '',
      images: [],
      video: null
    } as {
      id: number
      description: string
      images: ImageWithComment[]
      video: File | null
    },
    validationSchema: Yup.object().shape({
      description: Yup.string()
    }),
    onSubmit: async (values) => {
      if (values.video) {
        uploadProgressStore.setMeta({
          fileName: values.video.name,
          fileIndex: 1,
          totalFiles: 1,
          estimatedTotal: values.video.size
        })
      }

      await updateProtocolRoom.mutateAsync({
        id: values.id,
        description: values.description,
        video: values.video as any
      })

      const images = values.images
      for (let index = 0; index < images.length; index++) {
        const imageWithComment = images[index]
        uploadProgressStore.setMeta({
          fileName: imageWithComment.file.name,
          fileIndex: index + 1,
          totalFiles: images.length,
          estimatedTotal: imageWithComment.file.size
        })
        await createProtocolRoomImages.mutateAsync({
          file: imageWithComment.file,
          comment: imageWithComment.comment || undefined
        })
      }

      roomUpdateForm.resetForm()
    }
  })

  const { createProtocolRoomImages } = useProtocolRoomImages(Number(id), roomUpdateForm.values.id)

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {roomsWithQuestions.length === 0 ? (
        <Typography variant='caption' fontWeight={700} display='block' mb={4}>
          Brak Pokoi
        </Typography>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: '100%' }} aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Pokój</TableCell>
                <TableCell align='right'>Opis</TableCell>
                <TableCell align='right'>Wideo</TableCell>
                {protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                  <TableCell align='right'>Pytania</TableCell>
                )}
                <TableCell align='center' sx={{ maxWidth: '100px' }}>
                  Zdjęcia
                </TableCell>
                {protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                  <TableCell align='center'>Zdjęcia ze zdania</TableCell>
                )}
                {protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                  <TableCell align='center'>Opis ze zdania</TableCell>
                )}
                {!isReadOnly && <TableCell align='right' />}
              </TableRow>
            </TableHead>
            <TableBody sx={{ width: '100%' }}>
              {roomsWithQuestions.map((room, key) => (
                <Fragment key={`${room.id}_${key}`}>
                  <TableRow
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      backgroundColor:
                        roomUpdateForm.values.id === room.id || qestionRoomInstance.id === room.id
                          ? 'rgba(0,0,0,0.1)'
                          : undefined
                    }}
                  >
                    <TableCell
                      component='th'
                      scope='row'
                      sx={{ fontWeight: '600', textTransform: 'uppercase' }}
                    >
                      {room.roomInstance.nameLocal ?? 'n/a'}
                    </TableCell>
                    <TableCell align='center'>
                      {acceptanceRoomsWithQuestions[key]?.description ?? 'n/a'}
                    </TableCell>
                    <TableCell align='right'>
                      {room.video ? (
                        <IconButton component='a' href={room.video} target='_blank'>
                          <Movie />
                        </IconButton>
                      ) : (
                        'n/a'
                      )}
                    </TableCell>
                    {protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                      <TableCell align='right'>
                        <Badge
                          invisible={!room.answersCount}
                          badgeContent={room.answersCount}
                          color='error'
                          sx={{ width: 'fit-content' }}
                        >
                          <IconButton
                            onClick={() =>
                              setQuestionRoomInstance(
                                qestionRoomInstance.id === room.id
                                  ? {
                                      id: -1,
                                      name: '',
                                      label: ''
                                    }
                                  : {
                                      id: room.id,
                                      name: room.roomInstance.name,
                                      label: `${room.roomInstance.nameLocal} - ${room.roomInstance.name}`
                                    }
                              )
                            }
                          >
                            <QuestionMark />
                          </IconButton>
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell align='center' sx={{ maxWidth: 400 }}>
                      {acceptanceRoomsWithQuestions[key] && (
                        <ProtocolRoomImages roomId={acceptanceRoomsWithQuestions[key].id} />
                      )}
                    </TableCell>
                    {protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                      <TableCell align='center' sx={{ maxWidth: 400 }}>
                        <ProtocolRoomImages roomId={room.id} />
                      </TableCell>
                    )}
                    {acceptanceRoomsWithQuestions[key] &&
                      protocol?.protocolType === PROTOCOL_TYPE.delivery && (
                        <TableCell align='center'>{room?.description ?? 'n/a'}</TableCell>
                      )}
                    {!isReadOnly && (
                      <TableCell align='right'>
                        <IconButton
                          onClick={() =>
                            roomUpdateForm.setValues({
                              id: room.id,
                              description: room.description ?? '',
                              images: [],
                              video: null
                            })
                          }
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    {roomUpdateForm.values.id === room.id && (
                      <>
                        <Typography
                          variant='caption'
                          fontWeight={700}
                          display='flex'
                          alignItems='center'
                        >
                          <IconButton size='small' onClick={() => roomUpdateForm.resetForm()}>
                            <Close />
                          </IconButton>
                          Edytuj wpis
                        </Typography>
                        <Box style={{ width: '200%', marginBottom: '20px' }}>
                          <form onSubmit={roomUpdateForm.handleSubmit}>
                            <TextField
                              error={!!roomUpdateForm.errors.description}
                              helperText={roomUpdateForm.errors.description}
                              fullWidth
                              multiline
                              rows={4}
                              sx={{ my: 2 }}
                              id='description'
                              label='Opis'
                              variant='outlined'
                              onChange={roomUpdateForm.handleChange}
                              value={roomUpdateForm.values.description}
                              disabled={roomUpdateForm.isSubmitting}
                            />
                            <Button component='label' variant='contained' startIcon={<Movie />}>
                              {roomUpdateForm.values.video?.name ?? 'Dodaj Wideo'}
                              <VisuallyHiddenInput
                                type='file'
                                accept='video/*'
                                onChange={(e: any) => {
                                  if (e.target.files.length) {
                                    roomUpdateForm.setFieldValue('video', e.target.files[0])
                                    return (e.target.value = '')
                                  }

                                  roomUpdateForm.setFieldValue('video', null)
                                  return (e.target.value = '')
                                }}
                              />
                            </Button>
                            <ProtocolRoomImagesUpload
                              multiple
                              disabled={roomUpdateForm.isSubmitting}
                              roomId={roomUpdateForm.values.id}
                              onAdd={(images) => {
                                const newImages = (images as File[]).map((file) => ({
                                  file,
                                  comment: ''
                                }))
                                roomUpdateForm.setFieldValue('images', [
                                  ...roomUpdateForm.values.images,
                                  ...newImages
                                ])
                              }}
                              onRemove={(imageKey) => {
                                roomUpdateForm.setFieldValue(
                                  'images',
                                  roomUpdateForm.values.images.filter((_, k) => k !== imageKey)
                                )
                              }}
                              onCommentChange={(imageKey, comment) => {
                                const updatedImages = [...roomUpdateForm.values.images]
                                updatedImages[imageKey] = {
                                  ...updatedImages[imageKey],
                                  comment
                                }
                                roomUpdateForm.setFieldValue('images', updatedImages)
                              }}
                              images={roomUpdateForm.values.images}
                            />
                            {createProtocolRoomImages.isError && (
                              <Typography color='red' variant='body2' mb={2}>
                                Upewnij się, że nazwa pliku ma długość co najwyżej 100 znaków, lub
                                plik nie jest za duży.
                              </Typography>
                            )}
                            {createProtocolRoomImages.isError && (
                              <Typography color='red' variant='body2' mb={2}>
                                Błąd zapisu. Spróbuj ponownie. Sprawdź poprawnośc danych, jeżeli
                                wysyłasz wideo sprawdź rozmiar oraz upewnij się, że nazwa pliku ma
                                długość co najwyżej 100 znaków
                              </Typography>
                            )}
                            <UploadProgress />
                            <Button
                              fullWidth
                              type='submit'
                              variant='contained'
                              loading={roomUpdateForm.isSubmitting}
                            >
                              Zapis
                            </Button>
                          </form>
                        </Box>
                      </>
                    )}
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ProtocolControlQuestions
        roomInstance={qestionRoomInstance}
        isReadOnly={isReadOnly}
        isOpen={qestionRoomInstance.id !== -1}
        handleClose={() =>
          setQuestionRoomInstance({
            id: -1,
            name: '',
            label: ''
          })
        }
      />
    </>
  )
}

export default ProtocolRooms
